import compact from 'lodash/compact';
import {
  isEmptyString,
  isNilOrZero,
} from '@populateui/utils';
import {
  FormCategoryDTO,
  FormFieldView,
  FormWorkflowDTO,
  FormWorkflowWithTransitionSuccessMessageResponseDTO,
  PatientVisitDTO,
  UserDTO,
} from 'dtos';
import {
  FormWorkflowStatus,
} from 'dtos/formWorkflowStatus';
import dayjs, {
  getDefaultTimeZoneId,
} from 'utils/dayjs';

export function resolveAssigneeFromVisit(
  visit: PatientVisitDTO | null,
  activeUsers: UserDTO[],
  fallback: UserDTO | null,
): UserDTO | null {
  const providerUserId = visit?.provider?.userId;

  return isNilOrZero(providerUserId)
    ? fallback
    : activeUsers.find((user) => user.userId === providerUserId) ?? fallback;
}

export function getFormWorkflowFromTransitionResponse(
  response: FormWorkflowWithTransitionSuccessMessageResponseDTO,
): FormWorkflowDTO | undefined {
  return response.workflow;
}

export function isOrderOrReferralCategory(category?: FormCategoryDTO | null): boolean {
  return category?.value === 'OrderForm' || category?.value === 'ReferralOutgoing';
}

export function formRequiresClinicianReview(
  fields: FormFieldView[],
  category?: FormCategoryDTO | null,
): boolean {
  return isOrderOrReferralCategory(category)
    ? true
    : fields.length > 0;
}

export function getFormWorkflowPatientMultiMediaIds(
  workflow: FormWorkflowDTO,
): number[] {
  return compact((workflow.medias ?? []).map((media) => media.patientMultiMediaId));
}

function stripHtmlTags(content: string): string {
  return content.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

export function parseWorkflowTokenFromComment(comment?: string | null): string | null {
  if (isEmptyString(comment ?? '')) {
    return null;
  }

  const match = stripHtmlTags(comment ?? '').match(/Open form workflow:\s*(\S+)/i);
  return match?.[1] ?? null;
}

export function getFormWorkflowStatusLabel(status?: string): string {
  switch (status) {
    case 'Completed':
      return 'Completed';
    case 'WaitingForPatient':
      return 'Sent to Patient';
    case 'WaitingForClinician':
      return 'Sent to Clinician';
    default:
      return 'Not Sent';
  }
}

export function getFormWorkflowStatusTooltip(
  status?: string,
  statusAt?: string | null,
): string | undefined {
  const label = getFormWorkflowStatusLabel(status);

  if (label === 'Not Sent') {
    return undefined;
  }

  if (isEmptyString(statusAt ?? '')) {
    return `${label} On: Unknown`;
  }

  const formattedStatusAt = dayjs(statusAt)
    .tz(getDefaultTimeZoneId())
    .format('M/D/YYYY');

  if (isEmptyString(formattedStatusAt)) {
    return `${label} On: Unknown`;
  }

  return `${label} On: ${formattedStatusAt}`;
}

export function getAssignedFormsDisabledTooltip(
  status?: FormWorkflowStatus | null,
): string {
  switch (status) {
    case FormWorkflowStatus.Completed:
      return 'These forms have been completed.';
    case FormWorkflowStatus.AwaitingPatient:
      return 'Clinician step is complete. These forms have been sent to the patient.';
    case FormWorkflowStatus.Cancelled:
      return 'This form workflow has been cancelled.';
    default:
      return 'These forms are no longer awaiting clinician action.';
  }
}
